import { useEffect, useState } from "react";
import { api, fmtINR, STATUS_COLORS, STATUS_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend, PieChart, Pie, Cell,
} from "recharts";
import { Link } from "react-router-dom";

const MONO = ["#00414E", "#005A6B", "#4A7480", "#8FA6AD", "#C4CFD3", "#E4E4E7"];

function Stat({ label, value, sub, tone = "default", testid }) {
  const toneClass = {
    default: "text-zinc-900",
    positive: "text-emerald-700",
    danger: "text-rose-700",
    warn: "text-amber-700",
  }[tone];
  return (
    <div className="p-6 bg-white border border-zinc-200" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`mt-2 text-3xl font-heading tabular-nums tracking-tight ${toneClass}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [centres, setCentres] = useState([]);
  const [trend, setTrend] = useState([]);
  const [cats, setCats] = useState([]);
  const [recent, setRecent] = useState([]);
  const [projectFilter, setProjectFilter] = useState("all");

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/kpis").then((r) => setKpis(r.data)),
      api.get("/dashboard/centre-summary").then((r) => setCentres(r.data)),
      api.get("/dashboard/monthly-trend").then((r) => setTrend(r.data)),
      api.get("/dashboard/category-breakdown").then((r) => setCats(r.data)),
      api.get("/expenses").then((r) => setRecent(r.data.slice(0, 8))),
    ]).catch(() => {});
  }, []);

  return (
    <div className="p-8 space-y-8" data-testid="dashboard-root">
      {/* Header */}
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Overview</div>
        <h1 className="font-heading text-4xl mt-1 tracking-tight">
          {user?.role === "director" ? "Management Console" :
            user?.role === "centre_manager" ? `${user?.name} · Centre Console` : "Finance Dashboard"}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Real-time centre-wise operating truth.</p>
      </div>

      {/* KPI strip */}
      <div className={`grid grid-cols-2 ${user?.role === "centre_manager" ? "lg:grid-cols-1 max-w-md" : "lg:grid-cols-4"} gap-4`}>
        {user?.role !== "centre_manager" && (
          <Stat testid="kpi-income" label="Total Income" value={fmtINR(kpis?.total_income)} sub={`Received: ${fmtINR(kpis?.received)}`} />
        )}
        <Stat testid="kpi-expense" label="Total Expense" value={fmtINR(kpis?.total_expense)} sub={`Paid: ${fmtINR(kpis?.total_paid)}`} />
        {user?.role !== "centre_manager" && (
          <Stat testid="kpi-pl" label="Net P/L" value={fmtINR(kpis?.net_pl)} tone={kpis?.net_pl >= 0 ? "positive" : "danger"} />
        )}
        {user?.role !== "centre_manager" && (
          <Stat testid="kpi-centres" label="Active Centres" value={kpis?.active_centres ?? "—"} sub={`${kpis?.pending_approvals || 0} pending approvals · ${kpis?.pending_payments || 0} to pay`} tone="warn" />
        )}
      </div>

      {/* Manager tiles — pending queues + this-month burn */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/expenses?status=submitted" className="block hover:opacity-90">
          <Stat
            testid="tile-pending-expenses"
            label="Pending Expenses"
            value={kpis?.pending_approvals ?? "—"}
            sub={`${kpis?.pending_payments || 0} awaiting payment`}
            tone={kpis?.pending_approvals > 0 ? "warn" : "default"}
          />
        </Link>
        <Link to="/assets" className="block hover:opacity-90">
          <Stat
            testid="tile-pending-assets"
            label="Pending Assets"
            value={kpis?.pending_assets ?? "—"}
            sub={`Approved value: ${fmtINR(kpis?.approved_assets_cost)}`}
            tone={kpis?.pending_assets > 0 ? "warn" : "default"}
          />
        </Link>
        <div>
          <Stat
            testid="tile-month-spend"
            label="This Month · Spend"
            value={fmtINR(kpis?.this_month_spend)}
            sub={`${kpis?.this_month_expense_count || 0} expenses recorded`}
          />
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 p-6 bg-white border border-zinc-200">
          <div className="flex justify-between items-baseline mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Income vs Expense</div>
              <div className="font-heading text-lg mt-1">Monthly Trend</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trend}>
              <CartesianGrid stroke="#E4E4E7" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#71717A" fontSize={11} />
              <YAxis stroke="#71717A" fontSize={11} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ border: "1px solid #09090B", borderRadius: 0, background: "#fff" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="income" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="expense" stroke="#09090B" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="p-6 bg-white border border-zinc-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Category Breakdown</div>
          <div className="font-heading text-lg mt-1 mb-6">Top Expense Types</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={cats.slice(0, 6)} dataKey="total" nameKey="category" innerRadius={40} outerRadius={80}>
                {cats.slice(0, 6).map((c, i) => <Cell key={c.category || `cell-${i}`} fill={MONO[i % MONO.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ border: "1px solid #09090B", borderRadius: 0, background: "#fff" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 space-y-1">
            {cats.slice(0, 4).map((c, i) => (
              <div key={c.category} className="flex justify-between text-xs">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 inline-block" style={{ background: MONO[i] }} />
                  {c.category}
                </span>
                <span className="tabular-nums font-medium">{fmtINR(c.total)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Centre performance table */}
      <div className="bg-white border border-zinc-200">
        <div className="flex justify-between items-center p-6 border-b border-zinc-200 gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Performance</div>
            <div className="font-heading text-lg mt-1">Centre-wise Summary</div>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            {user?.role !== "centre_manager" && centres.length > 0 && (
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="h-9 border border-zinc-300 rounded-sm text-xs px-2 bg-white"
                data-testid="dashboard-project-filter"
              >
                <option value="all">All Projects</option>
                {Array.from(new Set(centres.map((c) => c.project_name || "Unassigned"))).sort().map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            )}
            {user?.role !== "centre_manager" && (
              <Link to="/pnl" className="text-xs uppercase tracking-widest text-zinc-700 hover:text-black" data-testid="dashboard-view-pnl-link">View all P&L →</Link>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left p-4">Code</th>
                <th className="text-left p-4">Centre</th>
                <th className="text-left p-4">Project</th>
                <th className="text-left p-4">State</th>
                <th className="text-right p-4">Income</th>
                <th className="text-right p-4">Expense</th>
                <th className="text-right p-4">P/L</th>
                <th className="text-right p-4">Budget Utilisation</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {centres
                .filter((c) => projectFilter === "all" || (c.project_name || "Unassigned") === projectFilter)
                .map((c) => (
                <tr key={c.centre_id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="p-4 font-mono text-xs">{c.code}</td>
                  <td className="p-4 font-medium">{c.name}</td>
                  <td className="p-4 text-zinc-600">{c.project_name || <span className="text-zinc-400">Unassigned</span>}</td>
                  <td className="p-4 text-zinc-600">{c.state}</td>
                  <td className="p-4 text-right">{fmtINR(c.income)}</td>
                  <td className="p-4 text-right">{fmtINR(c.expense)}</td>
                  <td className={`p-4 text-right font-medium ${c.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtINR(c.profit)}</td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-24 h-1.5 bg-zinc-100">
                        <div className={`h-full ${c.utilisation > 90 ? "bg-rose-600" : c.utilisation > 75 ? "bg-amber-500" : "bg-emerald-600"}`}
                             style={{ width: `${Math.min(100, c.utilisation)}%` }} />
                      </div>
                      <span className="text-xs w-10 text-right">{c.utilisation}%</span>
                    </div>
                  </td>
                </tr>
              ))}
              {centres.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-zinc-400">No data yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent expenses */}
      <div className="bg-white border border-zinc-200">
        <div className="flex justify-between items-center p-6 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Activity</div>
            <div className="font-heading text-lg mt-1">Recent Expenses</div>
          </div>
          <Link to="/expenses" className="text-xs uppercase tracking-widest text-zinc-700 hover:text-black">View all →</Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-4">Expense No</th>
              <th className="text-left p-4">Centre</th>
              <th className="text-left p-4">Category</th>
              <th className="text-left p-4">Vendor</th>
              <th className="text-right p-4">Amount</th>
              <th className="text-left p-4">Status</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {recent.map((e) => (
              <tr key={e.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                <td className="p-4 font-mono text-xs">
                  <Link to={`/expenses/${e.id}`} className="hover:underline">{e.expense_no}</Link>
                </td>
                <td className="p-4">{e.centre_code}</td>
                <td className="p-4">{e.category}</td>
                <td className="p-4 text-zinc-600">{e.vendor || "—"}</td>
                <td className="p-4 text-right font-medium">{fmtINR(e.total_amount)}</td>
                <td className="p-4"><span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border ${STATUS_COLORS[e.status]}`}>{STATUS_LABEL[e.status]}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
